<template>
  <div class="fee-summary-container animated fadeInRight">
    <!-- 顶部：日期范围 + 日/月/年切换 + 快捷按钮 + 费用项筛选 -->
    <el-card class="search-card">
      <div slot="header" class="flex-between">
        <span>{{ $t('feeSummary.title') }}</span>
        <div class="header-controls">
          <el-button size="small" @click="_setQuickDate('today')">今天</el-button>
          <el-button size="small" @click="_setQuickDate('month')">本月</el-button>
          <el-button size="small" @click="_setQuickDate('year')">本年</el-button>
          <div class="type-toggle">
            <el-button size="small" :type="summaryType === 'day' ? 'primary' : ''" @click="_switchType('day')">日</el-button>
            <el-button size="small" :type="summaryType === 'month' ? 'primary' : ''" @click="_switchType('month')">月</el-button>
            <el-button size="small" :type="summaryType === 'year' ? 'primary' : ''" @click="_switchType('year')">年</el-button>
          </div>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            size="small"
            style="width: 280px"
            @change="_handleDateChange"
          />
          <el-select v-model="feeNameFilter" size="small" style="width: 160px; margin-left: 10px" placeholder="全部费用项" clearable @change="_computeAggregation">
            <el-option label="全部费用项" value="" />
            <el-option v-for="fee in feeOptions" :key="fee.feeName" :label="fee.feeName" :value="fee.feeName" />
          </el-select>
          <el-select v-model="groupBy" size="small" style="width: 140px; margin-left: 10px" @change="_computeAggregation">
            <el-option label="按费用类型" value="feeType" />
            <el-option label="按收费项目" value="feeConfig" />
            <el-option label="按支付方式" value="primeRate" />
          </el-select>
          <el-select v-model="dataSource" size="small" style="width: 130px; margin-left: 10px" clearable
            placeholder="数据来源" @change="_doQuery">
            <el-option label="全部" value="" />
            <el-option label="缴费" value="fee" />
            <el-option label="预存充值" value="prepay" />
          </el-select>
        </div>
      </div>
    </el-card>

    <!-- 汇总卡片 -->
    <el-row :gutter="16" class="summary-cards">
      <el-col :span="4">
        <div class="stat-card card-receivable">
          <div class="stat-num">{{ summary.receivable }}</div>
          <div class="stat-label">应收总额(元)</div>
        </div>
      </el-col>
      <el-col :span="4">
        <div class="stat-card card-received">
          <div class="stat-num">{{ summary.received }}</div>
          <div class="stat-label">实收总额(元)</div>
        </div>
      </el-col>
      <el-col :span="4">
        <div class="stat-card card-deduction">
          <div class="stat-num">{{ summary.deduction }}</div>
          <div class="stat-label">账户抵扣(元)</div>
        </div>
      </el-col>
      <el-col :span="4">
        <div class="stat-card card-discount">
          <div class="stat-num">{{ summary.preferential }}</div>
          <div class="stat-label">优惠减免(元)</div>
        </div>
      </el-col>
      <el-col :span="4">
        <div class="stat-card card-count">
          <div class="stat-num">{{ summary.count }}</div>
          <div class="stat-label">缴费笔数</div>
        </div>
      </el-col>
      <el-col :span="4">
        <div class="stat-card card-rate">
          <div class="stat-num">{{ summary.rate }}%</div>
          <div class="stat-label">实收率</div>
        </div>
      </el-col>
    </el-row>

    <!-- 分组汇总表格 -->
    <el-card class="table-card">
      <div slot="header" class="flex-between">
        <span>{{ groupBy === 'feeType' ? '按费用类型汇总' : groupBy === 'feeConfig' ? '按收费项目汇总' : '按支付方式汇总' }}</span>
        <el-button type="primary" size="small" @click="_exportData" :loading="exporting">
          {{ $t('common.export') }}
        </el-button>
      </div>

      <el-table :data="groupedData" border style="width: 100%" v-loading="loading" show-summary
        :summary-method="_getSummaries">
        <el-table-column prop="groupName" :label="groupBy === 'feeType' ? '费用类型' : groupBy === 'feeConfig' ? '收费项目' : '支付方式'" min-width="120" fixed />
        <el-table-column prop="receivableAmount" label="应收金额(元)" align="right" width="130" />
        <el-table-column prop="receivedAmount" label="实收金额(元)" align="right" width="130" />
        <el-table-column prop="deductionAmount" label="账户抵扣(元)" align="right" width="130" />
        <el-table-column prop="preferentialAmount" label="优惠减免(元)" align="right" width="130" />
        <el-table-column prop="giftAmount" label="赠送金额(元)" align="right" width="130" />
        <el-table-column prop="lateFee" label="滞纳金(元)" align="right" width="120" />
        <el-table-column prop="count" label="笔数" align="center" width="80" />
        <el-table-column label="占比" align="center" width="100">
          <template slot-scope="scope">
            <el-progress :percentage="scope.row.percent" :stroke-width="8" :show-text="true" />
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { queryPayFeeDetail } from '@/api/report/reportPayFeeDetailApi'
import { listFeeConfigs } from '@/api/fee/feeConfigManageApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'FeeSummaryList',
  data() {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    return {
      loading: false,
      exporting: false,
      communityId: '',
      summaryType: 'day',
      groupBy: 'feeType',
      feeNameFilter: '',
      dataSource: '',
      feeOptions: [],
      dateRange: [`${y}-${m}-${d}`, `${y}-${m}-${d}`],
      allRecords: [],
      groupedData: [],
      summary: {
        receivable: '0.00',
        received: '0.00',
        deduction: '0.00',
        preferential: '0.00',
        count: 0,
        rate: '0.00'
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._loadFeeOptions()
    this._doQuery()
  },
  methods: {
    async _loadFeeOptions() {
      try {
        const res = await listFeeConfigs({
          page: 1,
          row: 200,
          communityId: this.communityId,
          isDefault: 'F'
        })
        const configs = res.data || res.feeConfigs || []
        // 去重取费用名称
        const seen = new Set()
        this.feeOptions = configs.filter(c => {
          if (!c.feeName || seen.has(c.feeName)) return false
          seen.add(c.feeName)
          return true
        })
      } catch (e) {
        console.error('加载费用项失败:', e)
      }
    },
    _switchType(type) {
      this.summaryType = type
      this._setQuickDate(type === 'day' ? 'today' : type === 'month' ? 'month' : 'year')
    },
    _setQuickDate(preset) {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      if (preset === 'today') {
        this.summaryType = 'day'
        this.dateRange = [`${y}-${m}-${d}`, `${y}-${m}-${d}`]
      } else if (preset === 'month') {
        this.summaryType = 'month'
        const lastDay = new Date(y, parseInt(m), 0).getDate()
        this.dateRange = [`${y}-${m}-01`, `${y}-${m}-${String(lastDay).padStart(2, '0')}`]
      } else {
        this.summaryType = 'year'
        this.dateRange = [`${y}-01-01`, `${y}-12-31`]
      }
      this._doQuery()
    },
    _handleDateChange() {
      this._doQuery()
    },
    async _doQuery() {
      this.loading = true
      try {
        let startTime, endTime
        if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
          startTime = this.dateRange[0] + ' 00:00:00'
          endTime = this.dateRange[1] + ' 23:59:59'
        } else {
          // 兜底：dateRange 不是数组时用当天
          const today = this.dateRange || new Date().toISOString().substring(0, 10)
          startTime = today + ' 00:00:00'
          endTime = today + ' 23:59:59'
        }

        const params = {
          page: 1,
          row: 5000,
          communityId: this.communityId,
          startTime,
          endTime,
          state: '1400',
          dataSource: this.dataSource
        }

        const { data } = await queryPayFeeDetail(params)
        this.allRecords = data || []
        this._computeAggregation()
      } catch (error) {
        console.error('查询失败:', error)
        this.$message.error('查询失败')
      } finally {
        this.loading = false
      }
    },
    _computeAggregation() {
      let records = this.allRecords
      // 按费用项筛选
      if (this.feeNameFilter) {
        records = records.filter(r => r.feeName === this.feeNameFilter)
      }

      // 按 (feeId + startTime + endTime) 去重应收金额：
      // 同一条费用同一周期多次部分缴费时，应收只计一次
      const periodReceivable = {} // key: feeId||startTime||endTime → { amount, groupKey }

      const groups = {}

      records.forEach(item => {
        let groupKey
        if (this.groupBy === 'feeType') {
          groupKey = item.feeTypeCdName || item.feeName || '其他'
        } else if (this.groupBy === 'feeConfig') {
          groupKey = item.feeName || item.feeTypeCdName || '其他'
        } else {
          groupKey = this._normalizePrimeRate(item.primeRate) || '其他'
        }

        if (!groups[groupKey]) {
          groups[groupKey] = {
            groupName: groupKey,
            receivableAmount: 0,
            receivedAmount: 0,
            deductionAmount: 0,
            preferentialAmount: 0,
            giftAmount: 0,
            lateFee: 0,
            count: 0
          }
        }

        const g = groups[groupKey]
        g.receivedAmount += parseFloat(item.receivedAmount || 0)
        g.deductionAmount += parseFloat(item.withholdAmount || 0)
        g.preferentialAmount += parseFloat(item.preferentialAmount || 0) + parseFloat(item.deductionAmount || 0)
        g.giftAmount += parseFloat(item.giftAmount || 0)
        g.lateFee += parseFloat(item.lateFee || 0)
        g.count++

        // 按周期去重应收
        const pKey = item.feeId + '||' + item.startTime + '||' + item.endTime
        if (!periodReceivable[pKey]) {
          periodReceivable[pKey] = {
            amount: parseFloat(item.receivableAmount || 0),
            groupKey: groupKey
          }
        }
      })

      // 将去重后的应收金额加到对应分组
      Object.values(periodReceivable).forEach(p => {
        if (groups[p.groupKey]) {
          groups[p.groupKey].receivableAmount += p.amount
        }
      })
      let result = Object.values(groups)
      const totalReceived = result.reduce((s, g) => s + g.receivedAmount, 0)
      result.forEach(g => {
        g.receivableAmount = g.receivableAmount.toFixed(2)
        g.receivedAmount = g.receivedAmount.toFixed(2)
        g.deductionAmount = g.deductionAmount.toFixed(2)
        g.preferentialAmount = g.preferentialAmount.toFixed(2)
        g.giftAmount = g.giftAmount.toFixed(2)
        g.lateFee = g.lateFee.toFixed(2)
        g.percent = totalReceived > 0 ? parseFloat((g.receivedAmount / totalReceived * 100).toFixed(1)) : 0
      })

      // 按实收金额降序
      result.sort((a, b) => parseFloat(b.receivedAmount) - parseFloat(a.receivedAmount))
      this.groupedData = result

      // 汇总卡片
      const totalReceivable = result.reduce((s, g) => s + parseFloat(g.receivableAmount), 0)
      const totalDeduction = result.reduce((s, g) => s + parseFloat(g.deductionAmount), 0)
      const totalPreferential = result.reduce((s, g) => s + parseFloat(g.preferentialAmount), 0)
      const totalCount = result.reduce((s, g) => s + g.count, 0)

      this.summary = {
        receivable: totalReceivable.toFixed(2),
        received: totalReceived.toFixed(2),
        deduction: totalDeduction.toFixed(2),
        preferential: totalPreferential.toFixed(2),
        count: totalCount,
        rate: totalReceivable > 0 ? (totalReceived / totalReceivable * 100).toFixed(1) : '0.00'
      }
    },
    _normalizePrimeRate(raw) {
      if (!raw) return raw
      const num = parseFloat(raw)
      if (!isNaN(num)) {
        const map = { 1: '现金', 2: 'POS刷卡', 3: '微信', 4: '支付宝', 5: '转账', 100: '账户抵扣' }
        return map[num] || raw
      }
      return raw
    },
    _getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((col, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        const vals = data.map(item => {
          const prop = col.property
          if (prop === 'count') return item.count || 0
          if (prop === 'percent') return 0
          return parseFloat(item[prop] || 0)
        })
        const total = vals.reduce((s, v) => s + v, 0)
        if (col.property === 'count') {
          sums[index] = total
        } else if (col.property === 'percent') {
          sums[index] = ''
        } else {
          sums[index] = total.toFixed(2)
        }
      })
      return sums
    },
    async _exportData() {
      this.exporting = true
      try {
        // 导出当前汇总表格数据
        const headers = ['分组名称', '应收金额(元)', '实收金额(元)', '账户抵扣(元)', '优惠减免(元)', '赠送金额(元)', '滞纳金(元)', '笔数', '占比(%)']
        const rows = this.groupedData.map(row => [
          row.groupName,
          row.receivableAmount,
          row.receivedAmount,
          row.deductionAmount,
          row.preferentialAmount,
          row.giftAmount,
          row.lateFee,
          row.count,
          row.percent
        ])
        // 合计行
        const totalRow = ['合计',
          this.summary.receivable,
          this.summary.received,
          this.summary.deduction,
          this.summary.preferential,
          '0.00', '0.00',
          this.summary.count,
          this.summary.rate
        ]

        const csvContent = '\uFEFF' + [headers, ...rows, totalRow]
          .map(r => r.map(c => (typeof c === 'string' && c.includes(',') ? `"${c}"` : c)).join(','))
          .join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        const title = this.summaryType === 'day' ? '日报表' : this.summaryType === 'month' ? '月报表' : '年报表'
        const dateLabel = Array.isArray(this.dateRange)
          ? `${this.dateRange[0]}至${this.dateRange[1]}` : this.dateRange
        link.download = `缴费汇总-${title}-${dateLabel}.csv`
        link.click()
        URL.revokeObjectURL(link.href)
        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-summary-container {
  padding: 20px;

  .flex-between {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .type-toggle {
    display: inline-flex;
    .el-button + .el-button { margin-left: -1px; border-radius: 0; }
    .el-button:first-child { border-radius: 4px 0 0 4px; }
    .el-button:last-child { border-radius: 0 4px 4px 0; }
  }

  .summary-cards {
    margin-bottom: 20px;

    .stat-card {
      background: #fff;
      border-radius: 8px;
      padding: 18px 12px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      border-left: 4px solid #ccc;

      .stat-num {
        font-size: 26px;
        font-weight: bold;
        color: #333;
        margin-bottom: 4px;
      }
      .stat-label {
        font-size: 12px;
        color: #999;
      }

      &.card-receivable { border-color: #E6A23C; .stat-num { color: #E6A23C; } }
      &.card-received  { border-color: #67C23A; .stat-num { color: #67C23A; } }
      &.card-deduction { border-color: #409EFF; .stat-num { color: #409EFF; } }
      &.card-discount  { border-color: #909399; .stat-num { color: #909399; } }
      &.card-count    { border-color: #F56C6C; .stat-num { color: #F56C6C; } }
      &.card-rate     { border-color: #9C27B0; .stat-num { color: #9C27B0; } }
    }
  }

  .search-card { margin-bottom: 0; }
  .table-card  { margin-top: 0; }

  .el-pagination {
    margin-top: 20px;
    text-align: right;
  }
}
</style>
